/**
 * The left-hand palette: design primitives on top, then the prebuilt DVM
 * components (charts, tables, input controls) grouped below.
 *
 * Two ways to place an item, matching how design tools behave:
 * - **drag** onto the canvas — drops at the cursor, at the item's default size;
 * - **click** — arms the matching tool so the next canvas drag draws it.
 *
 * The widget entries are config-driven (`options.layoutTypes`), so adding a
 * component type to `dvm-layout-builder.json` surfaces it here automatically.
 */

import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, input, output, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';

import { PALETTE_GROUP_LABELS } from '../../design-builder.constants';
import {
  DESIGN_PALETTE_DRAG_TYPE,
  type DesignTool,
  type PaletteItem,
} from '../../design-builder.types';

interface PaletteSection {
  key: string;
  label: string;
  items: PaletteItem[];
}

@Component({
  selector: 'app-design-component-palette',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, FormsModule, MatIconModule, MatTooltipModule],
  templateUrl: './component-palette.component.html',
  styleUrl: './component-palette.component.scss',
})
export class ComponentPaletteComponent {
  readonly palette = input<readonly PaletteItem[]>([]);
  /** The armed tool, so the matching entry can show as active. */
  readonly activeTool = input<DesignTool | string>('select');

  /** Click-to-arm: the palette key the canvas should draw next. */
  readonly itemPicked = output<PaletteItem>();

  readonly search = signal('');

  readonly sections = computed<PaletteSection[]>(() => {
    const query = this.search().trim().toLowerCase();
    const matches = query
      ? this.palette().filter(
          (item) =>
            item.label.toLowerCase().includes(query) ||
            (item.layoutType ?? '').toLowerCase().includes(query),
        )
      : this.palette();

    const groups = new Map<string, PaletteItem[]>();
    for (const item of matches) {
      const list = groups.get(item.group) ?? [];
      list.push(item);
      groups.set(item.group, list);
    }

    // Fixed order — design primitives first, then charts, then inputs. The
    // screen is not listed: every design already has exactly one.
    return (['design', 'charts', 'inputs'] as const)
      .filter((key) => groups.get(key)?.length)
      .map((key) => ({
        key,
        label: PALETTE_GROUP_LABELS[key] ?? key,
        items: groups.get(key) ?? [],
      }));
  });

  readonly isEmpty = computed(() => !this.sections().length);

  onDragStart(item: PaletteItem, event: DragEvent): void {
    // The key alone identifies the item; the canvas resolves it against the
    // same palette, so nothing has to be serialised into the drag payload.
    event.dataTransfer?.setData(DESIGN_PALETTE_DRAG_TYPE, item.key);
    if (event.dataTransfer) event.dataTransfer.effectAllowed = 'copy';
  }

  onPick(item: PaletteItem): void {
    this.itemPicked.emit(item);
  }

  isActive(item: PaletteItem): boolean {
    return this.activeTool() === item.key;
  }

  trackByKey(_: number, item: PaletteItem): string {
    return item.key;
  }

  trackBySection(_: number, section: PaletteSection): string {
    return section.key;
  }
}
